import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toLocalISODate } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  Filter,
  CheckCircle2,
  XCircle,
  Info
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TimeRecords = () => {
  const { isWorker, isAdmin, isSupervisor } = useAuth();
  const { t, language } = useLanguage();
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [myEmployee, setMyEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    employee_id: '',
    start_date: toLocalISODate(new Date()),
    end_date: toLocalISODate(new Date())
  });

  useEffect(() => {
    initializeData();
  }, []);

  useEffect(() => {
    if (myEmployee || !isWorker()) {
      fetchRecords();
    }
  }, [filters, myEmployee]);

  const initializeData = async () => {
    try {
      if (isWorker()) {
        // Workers only see their own data
        const myEmpRes = await axios.get(`${API_URL}/employees/me`);
        setMyEmployee(myEmpRes.data);
        setFilters(prev => ({ ...prev, employee_id: myEmpRes.data.id }));
      } else {
        // Admins/Supervisors can see all employees
        const employeesRes = await axios.get(`${API_URL}/employees`);
        setAllEmployees(employeesRes.data);
        setEmployees(employeesRes.data.filter(e => e.is_active !== false));
      }
    } catch (error) {
      console.error('Error initializing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecords = async () => {
    try {
      const params = { ...filters };
      // For workers, always filter by their own employee_id
      if (isWorker() && myEmployee) {
        params.employee_id = myEmployee.id;
      }
      const recordsRes = await axios.get(`${API_URL}/time-records`, { params });
      setRecords(recordsRes.data);
    } catch (error) {
      console.error('Error fetching records:', error);
    }
  };

  const getEmployeeName = (employeeId) => {
    if (isWorker() && myEmployee) {
      return myEmployee.name;
    }
    const emp = allEmployees.find(e => e.id === employeeId) || employees.find(e => e.id === employeeId);
    return emp?.name || employeeId;
  };

  const getRecordTypeLabel = (type) => {
    const labels = {
      clock_in: t('clock_in'),
      clock_out: t('clock_out'),
      break_start: t('break_start'),
      break_end: t('break_end')
    };
    return labels[type] || type;
  };

  const getRecordTypeColor = (type) => {
    const colors = {
      clock_in: 'bg-emerald-100 text-emerald-800',
      clock_out: 'bg-red-100 text-red-800',
      break_start: 'bg-amber-100 text-amber-800',
      break_end: 'bg-blue-100 text-blue-800'
    };
    return colors[type] || 'bg-slate-100 text-slate-800';
  };

  const formatDateTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return {
        date: date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US'),
        time: date.toLocaleTimeString(language === 'es' ? 'es-ES' : 'en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        })
      };
    } catch {
      return { date: '-', time: '-' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-records-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('time_records')}</h1>
        <p className="text-slate-500">
          {isWorker() 
            ? (language === 'es' ? 'Tu historial de fichajes' : 'Your time record history')
            : (language === 'es' ? 'Registro de fichajes de empleados' : 'Employee time records')
          }
        </p>
      </div>

      {/* Worker info banner */}
      {isWorker() && myEmployee && (
        <div className="flex items-center gap-3 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
          <Info className="w-5 h-5 text-indigo-600" />
          <div>
            <p className="text-sm font-medium text-indigo-900">
              {language === 'es' ? 'Mis fichajes' : 'My time records'}
            </p>
            <p className="text-sm text-indigo-700">
              {language === 'es' 
                ? `Mostrando registros de: ${myEmployee.name}`
                : `Showing records for: ${myEmployee.name}`}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Employee filter - only for admin/supervisor */}
            {!isWorker() && (
              <div className="space-y-2">
                <Label>{t('employees')}</Label>
                <Select 
                  value={filters.employee_id} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, employee_id: value === 'all' ? '' : value }))}
                >
                  <SelectTrigger data-testid="filter-employee">
                    <SelectValue placeholder={language === 'es' ? 'Todos' : 'All'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>{t('start_date')}</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('end_date')}</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                data-testid="filter-end-date"
              />
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  const today = toLocalISODate(new Date());
                  setFilters(prev => ({
                    ...prev,
                    employee_id: isWorker() && myEmployee ? myEmployee.id : '',
                    start_date: today,
                    end_date: today
                  }));
                }}
              >
                <Filter className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Limpiar' : 'Clear'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {!isWorker() && <TableHead className="font-semibold">{t('employees')}</TableHead>}
                <TableHead className="font-semibold">{language === 'es' ? 'Fecha' : 'Date'}</TableHead>
                <TableHead className="font-semibold">{language === 'es' ? 'Hora' : 'Time'}</TableHead>
                <TableHead className="font-semibold">{language === 'es' ? 'Tipo' : 'Type'}</TableHead>
                <TableHead className="font-semibold">{language === 'es' ? 'Ubicación' : 'Location'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isWorker() ? 4 : 5} className="text-center py-8 text-slate-500">
                    {t('no_results')}
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record, index) => {
                  const { date, time } = formatDateTime(record.timestamp);
                  return (
                    <TableRow key={index} className="hover:bg-slate-50" data-testid={`record-row-${index}`}>
                      {!isWorker() && <TableCell className="font-medium">{getEmployeeName(record.employee_id)}</TableCell>}
                      <TableCell className="text-slate-600">{date}</TableCell>
                      <TableCell className="font-mono tabular-nums text-slate-900">{time}</TableCell>
                      <TableCell>
                        <Badge className={getRecordTypeColor(record.record_type)}>
                          {getRecordTypeLabel(record.record_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {record.location_valid ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className={record.location_valid ? 'text-emerald-600' : 'text-red-600'}>
                            {record.location_valid ? t('location_valid') : t('location_invalid')}
                          </span>
                          {record.location && (
                            <span className="text-xs text-slate-400">
                              ({record.location.latitude?.toFixed(4)}, {record.location.longitude?.toFixed(4)})
                            </span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TimeRecords;
